import {
    useContext,
    useState
} from "react";
import {
    View
} from "react-native";
import stylesheet from "./stylesheet";
import EditProfileModal from "./views/editProfileModal";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    PageContainer,
    Avatar,
    Button,
    Text
} from "ncore-ui-kit";
import {
    PencilIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

const Profile = () => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        updateUser,
        logout,
        user
    } = useContext(AuthContext);

    const getRoleText = (role?: string) => {
        switch (role) {
            case "root":
                return localize("role-root");
            case "superAdmin":
                return localize("role-superadmin");
            case "admin":
                return localize("role-admin");
            case "user":
                return localize("role-user");
            case "guest":
                return localize("role-guest");
            default:
                return role || "";
        }
    };

    const [
        isEditModalVisible,
        setIsEditModalVisible
    ] = useState(false);

    const handleSaveProfile = async (data: { profilePicturePath?: string; oldPassword?: string; newPassword?: string; address?: string; email?: string; phone?: string; }) => {
        try {
            const token = await AsyncStorage.getItem("authToken");
            const response = await fetch(`${process.env.API_URL}/users/profile`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(data),
                method: "PUT"
            });

            const result = await response.json();
            if (result.status === "success") {
                NCoreUIKitToast.open({
                    title: localize("org-form-toast-success"),
                    type: "success"
                });
                if (user && token) {
                    await updateUser({
                        profilePicturePath: data.profilePicturePath,
                        address: data.address,
                        email: data.email,
                        phone: data.phone
                    });
                }
                setIsEditModalVisible(false);
            } else {
                NCoreUIKitToast.open({
                    title: result.message || localize("org-form-toast-error"),
                    type: "danger"
                });
            }
        } catch (error) {
            console.error("Profil güncellenirken hata:", error);
            NCoreUIKitToast.open({
                title: localize("org-form-toast-error"),
                type: "danger"
            });
        }
    };

    return <PageContainer
        scrollViewProps={{
            showsHorizontalScrollIndicator: false,
            showsVerticalScrollIndicator: false
        }}
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    backgroundColor: colors.system.default.background
                }
            ]}
        >
            <View
                style={[
                    stylesheet.card,
                    {
                        backgroundColor: colors.content.container.default,
                        borderColor: colors.content.border.subtle,
                        padding: spaces.spacingXl * 2.5,
                        borderRadius: radiuses.md,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Avatar
                    title={user?.fullName || user?.name_surname || user?.username}
                    key={user?.profilePicturePath || "default-avatar"}
                    imageUrl={user?.profilePicturePath}
                    size="xxLarge"
                />
                <Text
                    style={[
                        stylesheet.textSpacing,
                        {
                            marginBottom: spaces.spacingSm
                        }
                    ]}
                    variant="titleLargeSize"
                    color="high"
                >
                    {user?.fullName || user?.name_surname || user?.username}
                </Text>
                {
                    user?.email && (
                        <Text
                            style={[
                                stylesheet.textSpacing,
                                {
                                    marginBottom: spaces.spacingSm
                                }
                            ]}
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {localize("users-preview-email", [
                                user.email
                            ])}
                        </Text>)
                }
                {
                    user?.phone && (
                        <Text
                            style={[
                                stylesheet.textSpacing,
                                {
                                    marginBottom: spaces.spacingSm
                                }
                            ]}
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {localize("users-preview-phone", [
                                user.phone
                            ])}
                        </Text>)
                }
                {
                    user?.address && (
                        <Text
                            style={[
                                stylesheet.textSpacing,
                                {
                                    marginBottom: spaces.spacingSm
                                }
                            ]}
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {localize("users-preview-address", [
                                user.address
                            ])}
                        </Text>)
                }
                <Text
                    style={[
                        stylesheet.textSpacing,
                        {
                            marginBottom: spaces.spacingSm
                        }
                    ]}
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {localize("users-list-role-label", [
                        getRoleText(user?.role)
                    ])}
                </Text>
                <Button
                    onPress={() => setIsEditModalVisible(true)}
                    title={localize("users-list-edit-button")}
                    icon={({
                        color,
                        size
                    }) => {
                        return <PencilIcon
                            color={colors.content.icon[color]}
                            size={size}
                        />;
                    }}
                    spreadBehaviour="free"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={localize("dashboard-logout-button")}
                    onPress={async () => {
                        await logout();
                    }}
                    spreadBehaviour="free"
                    type="danger"
                    size="small"
                />
            </View>
        </View>
        <EditProfileModal
            profilePicturePath={user?.profilePicturePath}
            onClose={() => setIsEditModalVisible(false)}
            username={user?.username || ""}
            isVisible={isEditModalVisible}
            onSave={handleSaveProfile}
            address={user?.address}
            email={user?.email}
            phone={user?.phone}
        />
    </PageContainer>;
};
export default Profile;
